import dayjs, { type Dayjs } from 'dayjs'

export const invalidateLogsSnapshot = <
  T extends { logsByFilter: Record<string, unknown> },
>(
  snapshot: T
): T => ({
  ...snapshot,
  logsByFilter: {},
})

export const invalidateLogsAndMetricsSnapshot = <
  T extends {
    logsByFilter: Record<string, unknown>
    metricsByFilter: Record<string, unknown>
  },
>(
  snapshot: T
): T => ({
  ...snapshot,
  logsByFilter: {},
  metricsByFilter: {},
})

export const pickSettledValue = <T>(
  result: PromiseSettledResult<T>,
  fallback: T
): T => (result.status === 'fulfilled' ? result.value : fallback)

export const findSettledError = (
  results: PromiseSettledResult<unknown>[]
): unknown =>
  results.find(
    (result): result is PromiseRejectedResult => result.status === 'rejected'
  )?.reason

export const findNewestRunStartedAt = (
  logs: Array<{ runStartedAt: string }>
): string | undefined =>
  logs.reduce<string | undefined>((newest, log) => {
    if (!dayjs(log.runStartedAt).isValid()) return newest
    return !newest || dayjs(log.runStartedAt).isAfter(newest)
      ? log.runStartedAt
      : newest
  }, undefined)

export const toLogsPagingUpperBound = (newestRunStartedAt?: string) =>
  newestRunStartedAt
    ? dayjs(newestRunStartedAt).startOf('second').add(1, 'second')
    : undefined

export const pickEarlierBound = (left?: Dayjs | null, right?: Dayjs | null) => {
  if (!left) return right ?? undefined
  if (!right) return left
  return left.isBefore(right) ? left : right
}

export const resolveLogsUpperBound = (
  userTo: Dayjs | null | undefined,
  newestRunStartedAt: string | undefined,
  now: Dayjs
) => {
  if (userTo?.isBefore(now)) return userTo
  return pickEarlierBound(userTo, toLogsPagingUpperBound(newestRunStartedAt))
}

export const appendUniqueLogs = <
  T extends { id: string; runStartedAt: string },
>(
  existing: T[],
  incoming: T[],
  newestRunStartedAt?: string
): T[] => {
  const knownIds = new Set(existing.map((log) => log.id))
  const isNewerThanFirstPage = (log: T) =>
    !!newestRunStartedAt && dayjs(log.runStartedAt).isAfter(newestRunStartedAt)
  return [
    ...existing,
    ...incoming.filter(
      (log) => !knownIds.has(log.id) && !isNewerThanFirstPage(log)
    ),
  ]
}
